import React, { useState } from 'react';
import { X } from 'lucide-react';

// Define props interface for GermanBrandsSlider
interface GermanBrandsSliderProps {
  language?: string;
}

// Define translations for GermanBrandsSlider content
const TRANSLATIONS: Record<string, {
  headline: string;
  subheadlineLine1: string;
  subheadlineLine2: string;
  viewMore: string;
  modalTitle: string;
  closeModal: string;
}> = {
  en: {
    headline: "Our Domains of Global Network",
    subheadlineLine1: "Explore the worldwide presence of Made in Germany",
    subheadlineLine2: "through our strategically established domain network.",
    viewMore: "View More Domains",
    modalTitle: "Complete Domain Network",
    closeModal: "Close"
  },
  de: {
    headline: "Unsere Domains des globalen Netzwerks",
    subheadlineLine1: "Erfahren Sie mehr über die weltweite Präsenz von Made in Germany",
    subheadlineLine2: "durch unser strategisch aufgebautes Domain-Netzwerk.",
    viewMore: "Mehr Domains anzeigen",
    modalTitle: "Komplettes Domain-Netzwerk",
    closeModal: "Schließen"
  },
  es: {
    headline: "Nuestros dominios de la red global",
    subheadlineLine1: "Explore la presencia mundial de Made in Germany",
    subheadlineLine2: "a través de nuestra red de dominios estratégicamente establecida.",
    viewMore: "Ver más dominios",
    modalTitle: "Red completa de dominios",
    closeModal: "Cerrar"
  },
  fr: {
    headline: "Nos domaines du réseau mondial",
    subheadlineLine1: "Découvrez la présence mondiale de Made in Germany",
    subheadlineLine2: "grâce à notre réseau de domaines stratégiquement établi.",
    viewMore: "Voir plus de domaines",
    modalTitle: "Réseau de domaines complet",
    closeModal: "Fermer"
  },
  it: {
    headline: "I nostri domini della rete globale",
    subheadlineLine1: "Esplora la presenza mondiale di Made in Germany",
    subheadlineLine2: "attraverso la nostra rete di domini strategicamente stabilita.",
    viewMore: "Visualizza altri domini",
    modalTitle: "Rete di domini completa",
    closeModal: "Chiudere"
  },
  nl: {
    headline: "Onze domeinen van het wereldwijde netwerk",
    subheadlineLine1: "Ontdek de wereldwijde aanwezigheid van Made in Germany",
    subheadlineLine2: "via ons strategisch opgebouwde domeinnetwerk.",
    viewMore: "Meer domeinen bekijken",
    modalTitle: "Volledig domeinnetwerk",
    closeModal: "Sluiten"
  },
  sa: {
    headline: "مجالاتنا في الشبكة العالمية",
    subheadlineLine1: "استكشف الوجود العالمي لـ Made in Germany",
    subheadlineLine2: "من خلال شبكة المجالات التي أنشأناها استراتيجيًا.",
    viewMore: "عرض المزيد من المجالات",
    modalTitle: "شبكة المجالات الكاملة",
    closeModal: "إغلاق"
  },
  hk: {
    headline: "我們的全球網絡域名",
    subheadlineLine1: "探索 Made in Germany 的全球影響力",
    subheadlineLine2: "通過我們策略性建立的域名網絡。",
    viewMore: "查看更多域名",
    modalTitle: "完整域名網絡",
    closeModal: "關閉"
  },
  sg: {
    headline: "我们的全球网络域名",
    subheadlineLine1: "探索 Made in Germany 的全球影响力",
    subheadlineLine2: "通过我们策略性建立的域名网络。",
    viewMore: "查看更多域名",
    modalTitle: "完整域名网络",
    closeModal: "关闭"
  },
  za: {
    headline: "Ons domeine van die wêreldwye netwerk",
    subheadlineLine1: "Ontdek die wêreldwye teenwoordigheid van Made in Germany",
    subheadlineLine2: "deur ons strategies opgestelde domeinnetwerk.",
    viewMore: "Bekyk meer domeine",
    modalTitle: "Volledige domeinnetwerk",
    closeModal: "Sluit"
  },
  kr: {
    headline: "우리의 글로벌 네트워크 도메인",
    subheadlineLine1: "Made in Germany의 전 세계적 입지를 알아보세요",
    subheadlineLine2: "전략적으로 구축된 도메인 네트워크를 통해.",
    viewMore: "더 많은 도메인 보기",
    modalTitle: "전체 도메인 네트워크",
    closeModal: "닫기"
  },
  jp: {
    headline: "我々のグローバルネットワークのドメイン",
    subheadlineLine1: "Made in Germanyの世界的な存在感をご覧ください",
    subheadlineLine2: "戦略的に構築されたドメインネットワークを通じて。",
    viewMore: "さらにドメインを表示",
    modalTitle: "完全なドメインネットワーク",
    closeModal: "閉じる"
  },
};

// Language data from LanguageModal
const LANGUAGES = [
  { code: 'de', name: 'Deutsch', flag: '/flag/germany.gif' },
  { code: 'en', name: 'English', flag: '/flag/uk.gif' },
  { code: 'es', name: 'Español', flag: '/flag/spain.gif' },
  { code: 'fr', name: 'Français', flag: '/flag/french.gif' },
  { code: 'it', name: 'Italiano', flag: '/flag/italy.gif' },
  { code: 'nl', name: 'Dutch', flag: '/flag/netherlands.gif' },
  { code: 'sa', name: 'العربية (السعودية)', flag: '/flag/saudi.gif' },
  { code: 'ae', name: 'العربية', flag: '/flag/arab.gif' },
  { code: 'hk', name: '繁體中文', flag: '/flag/hong-kong.gif' },
  { code: 'tw', name: '正體中文', flag: '/flag/taiwan.gif' },
  { code: 'sg', name: '简体中文', flag: '/flag/singapore.gif' },
  { code: 'za', name: 'Afrikaans', flag: '/flag/south-africa.gif' }
];

// Domain-to-flag mapping
const DOMAIN_FLAGS: Record<string, string> = {
  "made-in-germany.world": '/flag/globe.webp', // Placeholder for global
  "made-in-germany-first.com": '/flag/germany.gif',
  "made-in-germany.asia": '/flag/singapore.gif', // Proxy for Asia
  "made-in-germany.africa": '/flag/south-africa.gif',
  "made-in-germany.lat": '/flag/spain.gif', // Proxy for Latin America
  "made-in-germany-arab.com": '/flag/saudi.gif',
  "made-in-germany-arabia.com": '/flag/arab.gif',
  "made-in-germany-china.com": '/flag/singapore.gif',
  "made-in-germany.my": '/flag/malaysia.webp', // Placeholder
  "made-in-germany.com.in": '/flag/india.webp', // Placeholder
  "madeingermany.in": '/flag/india.webp',
  // Modal domains
  "de.made-in-germany.global": '/flag/germany.gif',
  "fr.made-in-germany.global": '/flag/french.gif',
  "es.made-in-germany.global": '/flag/spain.gif',
  "it.made-in-germany.global": '/flag/italy.gif',
  "nl.made-in-germany.global": '/flag/netherlands.gif',
  "pl.made-in-germany.global": '/flag/poland.webp', // Placeholder
  "se.made-in-germany.global": '/flag/sweden.webp', // Placeholder
  "ch.made-in-germany.global": '/flag/switzerland.webp', // Placeholder
  "at.made-in-germany.global": '/flag/austria.webp', // Placeholder
  "uk.made-in-germany.global": '/flag/uk.gif',
  "us.made-in-germany.global": '/flag/uk.gif', // Proxy for English-speaking
  "ca.made-in-germany.global": '/flag/canada.webp', // Placeholder
  "mx.made-in-germany.global": '/flag/mexico.webp', // Placeholder
  "br.made-in-germany.global": '/flag/brazil.webp', // Placeholder
  "ar.made-in-germany.global": '/flag/argentina.webp', // Placeholder
  "cn.made-in-germany.global": '/flag/singapore.gif',
  "in.made-in-germany.global": '/flag/india.webp',
  "jp.made-in-germany.global": '/flag/japan.webp', // Placeholder
  "kr.made-in-germany.global": '/flag/korea.webp', // Placeholder
  "id.made-in-germany.global": '/flag/indonesia.webp', // Placeholder
  "th.made-in-germany.global": '/flag/thailand.webp', // Placeholder
  "vn.made-in-germany.global": '/flag/vietnam.webp', // Placeholder
  "ae.made-in-germany.global": '/flag/arab.gif',
  "sa.made-in-germany.global": '/flag/saudi.gif',
  "eg.made-in-germany.global": '/flag/egypt.webp', // Placeholder
  "za.made-in-germany.global": '/flag/south-africa.gif',
  "ng.made-in-germany.global": '/flag/nigeria.webp', // Placeholder
};

const GermanBrandsSlider: React.FC<GermanBrandsSliderProps> = ({ language = 'en' }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // Main domains to display
  const mainDomains = [
    "made-in-germany.world",
    "made-in-germany-first.com",
    "made-in-germany.asia",
    "made-in-germany.africa",
    "made-in-germany.lat",
    "made-in-germany-arab.com",
    "made-in-germany-arabia.com",
    "made-in-germany-china.com",
    "made-in-germany.my",
    "made-in-germany.com.in",
    "madeingermany.in",
    "VIEW_MORE_BUTTON",
  ];

  // Domains for the modal
  const regionaleDomains = [
    "made-in-germany.asia",
    "made-in-germany.africa",
    "made-in-germany.lat",
    "made-in-germany-arab.com",
  ];

  const subdomainsEuropa = [
    "de.made-in-germany.global",
    "fr.made-in-germany.global",
    "es.made-in-germany.global",
    "it.made-in-germany.global",
    "nl.made-in-germany.global",
    "pl.made-in-germany.global",
    "se.made-in-germany.global",
    "ch.made-in-germany.global",
    "at.made-in-germany.global",
    "uk.made-in-germany.global",
  ];

  const subdomainsAmerika = [
    "us.made-in-germany.global",
    "ca.made-in-germany.global",
    "mx.made-in-germany.global",
    "br.made-in-germany.global",
    "ar.made-in-germany.global",
  ];

  const subdomainsAsien = [
    "cn.made-in-germany.global",
    "in.made-in-germany.global",
    "jp.made-in-germany.global",
    "kr.made-in-germany.global",
    "id.made-in-germany.global",
    "th.made-in-germany.global",
    "vn.made-in-germany.global",
  ];

  const subdomainsNahostAfrika = [
    "ae.made-in-germany.global",
    "sa.made-in-germany.global",
    "eg.made-in-germany.global",
    "za.made-in-germany.global",
    "ng.made-in-germany.global",
  ];

  const translations = TRANSLATIONS[language] || TRANSLATIONS['en'];

  const totalSlides = Math.ceil(mainDomains.length / 12);
  
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev === totalSlides - 1 ? 0 : prev + 1));
  };
  
  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? totalSlides - 1 : prev - 1));
  };
  
  const getCurrentDomains = () => {
    const start = currentSlide * 12;
    const end = start + 12;
    return mainDomains.slice(start, end);
  };

  return (
    <div className="relative">
      {/* Main Section */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-950 rounded-xl shadow-2xl w-full max-w-7xl mx-auto mt-10 ring-1 ring-white/10 relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute inset-0 bg-grid-white/[0.02] -z-10" />
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-yellow-500 rounded-full blur-3xl opacity-10" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500 rounded-full blur-3xl opacity-10" />

        <div className="max-w-6xl mx-auto py-9 pb-8">
          <h2 className="text-xl sm:text-2xl font-bold text-center mb-4 bg-gradient-to-r from-white to-white/90 bg-clip-text text-transparent">
            {translations.headline}
          </h2>
          <p className="text-sm sm:text-base text-white/90 text-center mb-8">
            {translations.subheadlineLine1}
            <br />
            {translations.subheadlineLine2}
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 px-4 sm:px-6">
            {getCurrentDomains().map((item, index) => (
              item === "VIEW_MORE_BUTTON" ? (
                <button
                  key={index}
                  onClick={() => setModalOpen(true)}
                  className="group flex items-center justify-center gap-3 p-3 sm:p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-all duration-200 relative overflow-hidden ring-1 ring-white/10 hover:ring-white/20 hover:shadow-lg hover:shadow-white/5"
                >
                  <span className="text-sm sm:text-base text-white/90 font-medium group-hover:text-white transition-colors duration-200">
                    {translations.viewMore}
                  </span>
                </button>
              ) : (
                <div
                  key={index}
                  className="group flex items-center gap-3 p-3 sm:p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-all duration-200 relative overflow-hidden ring-1 ring-white/10 hover:ring-white/20 hover:shadow-lg hover:shadow-white/5"
                >
                  <div className="w-8 h-8 flex-shrink-0 rounded-lg overflow-hidden shadow-lg shadow-black/50 group-hover:shadow-white/20 transition-shadow duration-200">
                    <img
                      src={DOMAIN_FLAGS[item] || '/flag/default.webp'}
                      alt={`${item} flag`}
                      className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-200"
                    />
                  </div>
                  <a
                    href={`https://${item}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm sm:text-base text-white/90 font-medium group-hover:text-white transition-colors duration-200 text-wrap"
                  >
                    {item}
                  </a>
                </div>
              )
            ))}
          </div>
          
          {/* Navigation Controls */}
          {totalSlides > 1 && (
            <div className="flex justify-center items-center gap-3 mt-6">
              <button 
                onClick={prevSlide}
                className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-all duration-200 ring-1 ring-white/10 hover:ring-white/20"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white/90" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <span className="text-white/90 text-sm">
                {currentSlide + 1} / {totalSlides}
              </span>
              <button 
                onClick={nextSlide}
                className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-all duration-200 ring-1 ring-white/10 hover:ring-white/20"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white/90" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Modal */}
      {modalOpen && (
        <div 
          className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        >
          <div className="bg-gradient-to-br from-gray-900 to-gray-950 rounded-xl shadow-2xl w-full max-w-4xl h-auto max-h-[90vh] ring-1 ring-white/10 relative">
            {/* Modal Header */}
            <div className="sticky top-0 px-4 sm:px-6 py-4 flex items-center justify-between border-b border-white/10 bg-gray-900/50 backdrop-blur-sm z-10">
              <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-white to-white/90 bg-clip-text text-transparent">
                {translations.modalTitle}
              </h2>
              <button
                onClick={() => setModalOpen(false)}
                className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 transition-colors duration-200"
              >
                <X className="h-5 w-5 text-white/90" />
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="p-4 sm:p-6">
              <div className="grid grid-cols-1 gap-8">
                {/* Regionale & marktrelevante Domains */}
                <div>
                  <h4 className="text-xl text-white/90 mb-4 font-medium">Regionale & marktrelevante Domains</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                    {regionaleDomains.map((domain, index) => (
                      <div
                        key={index}
                        className="group flex items-center gap-3 p-3 sm:p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-all duration-200 relative overflow-hidden ring-1 ring-white/10 hover:ring-white/20 hover:shadow-lg hover:shadow-white/5"
                      >
                        <div className="w-8 h-8 flex-shrink-0 rounded-lg overflow-hidden shadow-lg shadow-black/50 group-hover:shadow-white/20 transition-shadow duration-200">
                          <img
                            src={DOMAIN_FLAGS[domain] || '/flag/default.webp'}
                            alt={`${domain} flag`}
                            className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-200"
                          />
                        </div>
                        <a
                          href={`https://${domain}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-white/90 font-medium group-hover:text-white transition-colors duration-200 text-wrap whitespace-nowrap"
                        >
                          {domain}
                        </a>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Subdomains: Europa */}
                <div>
                  <h4 className="text-xl text-white/90 mb-4 font-medium">Subdomains: Europa</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                    {subdomainsEuropa.map((domain, index) => (
                      <div
                        key={index}
                        className="group flex items-center gap-3 p-3 sm:p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-all duration-200 relative overflow-hidden ring-1 ring-white/10 hover:ring-white/20 hover:shadow-lg hover:shadow-white/5"
                      >
                        <div className="w-8 h-8 flex-shrink-0 rounded-lg overflow-hidden shadow-lg shadow-black/50 group-hover:shadow-white/20 transition-shadow duration-200">
                          <img
                            src={DOMAIN_FLAGS[domain] || '/flag/default.webp'}
                            alt={`${domain} flag`}
                            className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-200"
                          />
                        </div>
                        <a
                          href={`https://${domain}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-white/90 font-medium group-hover:text-white transition-colors duration-200 text-wrap whitespace-nowrap"
                        >
                          {domain}
                        </a>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Subdomains: Amerika */}
                <div>
                  <h4 className="text-xl text-white/90 mb-4 font-medium">Subdomains: Amerika</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                    {subdomainsAmerika.map((domain, index) => (
                      <div
                        key={index}
                        className="group flex items-center gap-3 p-3 sm:p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-all duration-200 relative overflow-hidden ring-1 ring-white/10 hover:ring-white/20 hover:shadow-lg hover:shadow-white/5"
                      >
                        <div className="w-8 h-8 flex-shrink-0 rounded-lg overflow-hidden shadow-lg shadow-black/50 group-hover:shadow-white/20 transition-shadow duration-200">
                          <img
                            src={DOMAIN_FLAGS[domain] || '/flag/default.webp'}
                            alt={`${domain} flag`}
                            className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-200"
                          />
                        </div>
                        <a
                          href={`https://${domain}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-white/90 font-medium group-hover:text-white transition-colors duration-200 text-wrap whitespace-nowrap"
                        >
                          {domain}
                        </a>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Subdomains: Asien */}
                <div>
                  <h4 className="text-xl text-white/90 mb-4 font-medium">Subdomains: Asien</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                    {subdomainsAsien.map((domain, index) => (
                      <div
                        key={index}
                        className="group flex items-center gap-3 p-3 sm:p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-all duration-200 relative overflow-hidden ring-1 ring-white/10 hover:ring-white/20 hover:shadow-lg hover:shadow-white/5"
                      >
                        <div className="w-8 h-8 flex-shrink-0 rounded-lg overflow-hidden shadow-lg shadow-black/50 group-hover:shadow-white/20 transition-shadow duration-200">
                          <img
                            src={DOMAIN_FLAGS[domain] || '/flag/default.webp'}
                            alt={`${domain} flag`}
                            className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-200"
                          />
                        </div>
                        <a
                          href={`https://${domain}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-white/90 font-medium group-hover:text-white transition-colors duration-200 text-wrap whitespace-nowrap"
                        >
                          {domain}
                        </a>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Subdomains: Nahost & Afrika */}
                <div>
                  <h4 className="text-xl text-white/90 mb-4 font-medium">Subdomains: Nahost & Afrika</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                    {subdomainsNahostAfrika.map((domain, index) => (
                      <div
                        key={index}
                        className="group flex items-center gap-3 p-3 sm:p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-all duration-200 relative overflow-hidden ring-1 ring-white/10 hover:ring-white/20 hover:shadow-lg hover:shadow-white/5"
                      >
                        <div className="w-8 h-8 flex-shrink-0 rounded-lg overflow-hidden shadow-lg shadow-black/50 group-hover:shadow-white/20 transition-shadow duration-200">
                          <img
                            src={DOMAIN_FLAGS[domain] || '/flag/default.webp'}
                            alt={`${domain} flag`}
                            className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-200"
                          />
                        </div>
                        <a
                          href={`https://${domain}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-white/90 font-medium group-hover:text-white transition-colors duration-200 text-wrap whitespace-nowrap"
                        >
                          {domain}
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GermanBrandsSlider;